import { useMemo } from "react";
import {
  createFileRoute,
  retainSearchParams,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { z } from "zod";

import type { RouterContextType } from "@/api/permissions/permissions.api";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import { formatProcurementRequestNumber } from "@/api/procurement/procurement.types";
import { TicketDetailPage } from "@/components/features/Procurement/design/TicketDetailPage";
import { usePermissions } from "@/hooks/auth/usePermissions";
import { useGetProcurementRequestDetail } from "@/hooks/procurement/useProcurement";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { authGuardWithPermissions } from "@/utils/authGuard";
import {
  leaveRecordTenant,
  RECORD_TENANT_SEARCH_FIELDS,
  syncRecordTenant,
} from "@/utils/recordTenantRoute";
import { getRouteIcon } from "@/utils/getRouteIconComponent";

const detailSearchSchema = z.object({
  tab: z.string().optional().catch(undefined),
  /** Opens a commitment action straight from the queue, so the row's button lands on the flow. */
  action: z.enum(["extend", "close"]).optional().catch(undefined),
  /** SERV-2975: which pricing line to open, on a ticket raised with several items. */
  line: z.string().optional().catch(undefined),
  ...RECORD_TENANT_SEARCH_FIELDS,
});

export const Route = createFileRoute("/_authenticated/procurement/$requestId")({
  component: ProcurementRequestDetailPage,
  validateSearch: detailSearchSchema.parse,
  search: { middlewares: [retainSearchParams(["tenant"])] },
  onEnter: syncRecordTenant,
  onStay: syncRecordTenant,
  onLeave: leaveRecordTenant,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

function ProcurementRequestDetailPage() {
  const { requestId } = Route.useParams();
  const { tab, action, line } = useSearch({ from: "/_authenticated/procurement/$requestId" });
  const navigate = useNavigate();
  const { data: requestDetail } = useGetProcurementRequestDetail(requestId);
  /* SERV-3166: read here and passed down, as procurement/index.tsx does for the queue's canCreate. */
  const { hasAccess } = usePermissions();
  const { edit: canWrite } = hasAccess({ module: MODULE_KEYS_MAP.procurement });

  const requestLabel = requestDetail
    ? formatProcurementRequestNumber(
        requestDetail.entityAbbreviation ?? null,
        requestDetail.requestNumber,
      )
    : "Loading...";

  const breadcrumbItems = useMemo(
    () => [
      {
        title: "Procurement Queue",
        href: "/procurement",
        icon: getRouteIcon(MODULE_KEYS_MAP.procurement),
      },
      { title: requestLabel, href: `/procurement/${requestId}` },
    ],
    [requestLabel, requestId],
  );

  useBreadcrumb(breadcrumbItems, { listView: "List View" });

  return (
    <TicketDetailPage
      key={requestId}
      ticketId={requestId}
      canWrite={canWrite}
      initialTab={tab}
      initialAction={action}
      pricingLineId={line}
      onPricingLineChange={(next) =>
        void navigate({
          to: "/procurement/$requestId",
          params: { requestId },
          search: (prev) => ({ ...prev, line: next }),
          replace: true,
        })
      }
    />
  );
}
